import { createSelector } from 'reselect';

// NOTE: Use these to make sure reference don't change
const emptyList = [];
const emptyObject = {};
const emptyComparativeView = { securities: [], criteria: [] };
const emptyEquityFinancials = { data: [], format: [] };

// PROPS ACCESSOR

const tickerFromProps = (state, props) => props.ticker;

const equityFinancialsTypeFromProps = (state, props) => props.equityFinancialsType;

// SIMPLE SELECTORS

export const comparativeActiveViewIdSelector = ({ domainData }) => (
    domainData.comparativeActiveViewId
);

export const equityActiveTickerSelector = ({ domainData }) => domainData.equityActiveTicker;

export const topGainersSelector = ({ domainData }) => (
    domainData.topGainers || emptyList
);

export const topLosersSelector = ({ domainData }) => (
    domainData.topLosers || emptyList
);

export const moversSelector = ({ domainData }) => (
    domainData.movers || emptyList
);

export const highestTurnoverSelector = ({ domainData }) => (
    domainData.highestTurnover || emptyList
);

export const mostActiveSelector = ({ domainData }) => (
    domainData.mostActive || emptyList
);

export const topBrokersSelector = ({ domainData }) => (
    domainData.topBrokers || emptyList
);

export const sectorsSelector = ({ domainData }) => (
    domainData.sectors || emptyObject
);

export const marketNotificationsSelector = ({ domainData }) => (
    domainData.marketNotifications || emptyList
);

export const priceActionsSelector = ({ domainData }) => (
    domainData.priceActions || emptyObject
);

export const portfolioSelector = ({ domainData }) => (
    domainData.portfolio || emptyObject
);

export const currentTimeSelector = ({ domainData }) => (
    domainData.currentTime
);

export const currentDateSelector = ({ domainData }) => (
    domainData.currentDate
);

export const watchlistTickersSelector = ({ domainData }) => (
    domainData.watchlistTickers || emptyList
);

export const tickerDetailsSelector = ({ domainData }) => (
    domainData.tickerDetail || emptyObject
);

export const tickerDetailSectorSelector = ({ domainData }) => (
    domainData.tickerDetailSector || emptyObject
);

export const upcomingEventsSelector = ({ domainData }) => (
    domainData.upcomingEvents || emptyList
);

export const todaysEventsSelector = ({ domainData }) => (
    domainData.todaysEvents || emptyList
);

export const todayAnnouncementsSelector = ({ domainData }) => (
    domainData.todayAnnouncements || emptyList
);

export const nepseCircularSelector = ({ domainData }) => (
    domainData.announcements.nepseCircular || emptyList
);

export const financialsSelector = ({ domainData }) => (
    domainData.announcements.financials || emptyList
);

export const ipoFpoSelector = ({ domainData }) => (
    domainData.announcements.ipoFpo || emptyList
);

export const rightSharesSelector = ({ domainData }) => (
    domainData.announcements.rightShares || emptyList
);

export const auctionSelector = ({ domainData }) => (
    domainData.announcements.auction || emptyList
);

export const bondDebentureSelector = ({ domainData }) => (
    domainData.announcements.bondDebenture || emptyList
);

export const agmSgmSelector = ({ domainData }) => (
    domainData.announcements.agmSgm || emptyList
);

export const dividendSelector = ({ domainData }) => (
    domainData.announcements.dividend || emptyList
);

export const dividendHistorySelector = ({ domainData }) => (
    domainData.equityDividendHistory || emptyObject
);

export const comparativeViewSelector = ({ domainData }) => (
    domainData.comparativeView || emptyObject
);

export const comparativeDataMapSelector = ({ domainData }) => (
    domainData.comparativeData || emptyObject
);

export const equityFinancialsSelector = ({ domainData }) => (
    domainData.equityFinancials || emptyObject
);

export const equityTimeSeriesData = ({ domainData }) => (
    domainData.equityTimeSeries || emptyObject
);

export const equityTearsheetSelector = ({ domainData }) => (
    domainData.equityTearsheet || emptyObject
);

export const equityKeyFinancialsSelector = ({ domainData }) => (
    domainData.equityKeyFinancials || emptyObject
);

export const equityAnnouncementSelector = ({ domainData }) => (
    domainData.equityAnnouncements || emptyObject
);

export const watchlistSelector = ({ domainData }) => (
    domainData.watchlist || emptyList
);

export const overviewActiveSectorSelector = ({ domainData }) => (
    domainData.overviewActiveSector
);

export const overviewTimeSeriesSelector = ({ domainData }) => (
    domainData.overviewTimeSeries || emptyObject
);

// COMPLEX SELECTORS

export const tickerDetailListSelector = createSelector(
    tickerDetailsSelector,
    tickerDetail => (Object.keys(tickerDetail).map(e =>
        ({
            ticker: e,
            ...tickerDetail[e],
        })) || emptyList),
);

export const tickerDetailForTickerSelector = createSelector(
    tickerFromProps,
    tickerDetailsSelector,
    (ticker, tickerDetail) => (tickerDetail[ticker] || emptyObject),
);

export const comparativeDataSelector = createSelector(
    comparativeDataMapSelector,
    comparativeActiveViewIdSelector,
    (comparativeDataMap, comparativeViewId) =>
        comparativeDataMap[comparativeViewId] || emptyObject,
);

export const comparativeViewOneSelector = createSelector(
    comparativeViewSelector,
    comparativeActiveViewIdSelector,
    (comparativeView, comparativeViewId) =>
        comparativeView[comparativeViewId] || emptyComparativeView,
);

// NOTE: shouldn't create objects as shown below inside the map
export const comparativeDataForViewOneSelector = createSelector(
    comparativeViewOneSelector,
    comparativeDataSelector,
    (comparativeViewOne, comparativeData) => comparativeViewOne.securities.map(
        ticker => comparativeData[ticker] || { ticker },
    ),
);

export const equityFinancialsTickerDataSelector = createSelector(
    equityFinancialsSelector,
    tickerFromProps,
    (data, ticker) => (data[ticker] || emptyObject),
);

export const equityPriceActionTickerSelector = createSelector(
    priceActionsSelector,
    tickerFromProps,
    (data, ticker) => (data[ticker] || emptyList),
);

export const equityFinancialsDataSelector = createSelector(
    equityFinancialsTickerDataSelector,
    equityFinancialsTypeFromProps,
    (data, financialType) => (data[financialType] || emptyEquityFinancials),
);

export const equityTickerDataSelector = createSelector(
    equityTearsheetSelector,
    tickerFromProps,
    (tearsheetData, ticker) => ((tearsheetData[ticker] || emptyObject).data || emptyObject),
);

export const equityTearsheetTickerTypeSelector = createSelector(
    equityTearsheetSelector,
    tickerFromProps,
    (tearsheetData, ticker) => (tearsheetData[ticker] || emptyObject).dataType,
);

export const dividendHistoryForTickerSelector = createSelector(
    dividendHistorySelector,
    tickerFromProps,
    (dividendHistory, ticker) => (dividendHistory[ticker] || emptyList),
);

export const equityTimeSeriesDataForTickerSelector = createSelector(
    equityTimeSeriesData,
    tickerFromProps,
    (data, ticker) => (data[ticker] || emptyObject),
);

export const equityKeyFinancialsForTickerSelector = createSelector(
    equityKeyFinancialsSelector,
    tickerFromProps,
    (keyFinancials, ticker) => (keyFinancials[ticker] || emptyList),
);

export const equityAnnouncementForTickerSelector = createSelector(
    equityAnnouncementSelector,
    tickerFromProps,
    (announcement, ticker) => (announcement[ticker] || emptyList),
);

export const equityTearsheetTickerKeyStatsVsIndustrySelector = createSelector(
    equityTickerDataSelector,
    data => (data.keyStatVsIndustry || emptyObject),
);

export const equityTearsheetTickerLiquidityIndicatorSelector = createSelector(
    equityTickerDataSelector,
    data => (data.liquidityIndicator || emptyObject),
);

export const equityTearsheetTickerChangeSinceLastQuaterSelector = createSelector(
    equityTickerDataSelector,
    data => (data.changeSinceLastQuater || emptyObject),
);

export const equityTearsheetTickerTechnicalIndicatorsSelector = createSelector(
    equityTickerDataSelector,
    data => (data.technicalIndicators || emptyObject),
);

export const equityTearsheetTickerStockInfoSelector = createSelector(
    equityTickerDataSelector,
    data => (data.stockInfo || emptyObject),
);

export const EquityMutualFundTickerSchemeInfoSelector = createSelector(
    equityTickerDataSelector,
    data => (data.schemeInfo || emptyObject),
);

export const EquityMutualFundTickerFundInformationSelector = createSelector(
    equityTickerDataSelector,
    data => (data.fundInfo || emptyObject),
);

export const EquityMutualFundTickerAssetAllocationSelector = createSelector(
    equityTickerDataSelector,
    data => (data.assetAllocation || emptyObject),
);

export const EquityMutualFundTickerAnnouncementSelector = createSelector(
    equityTickerDataSelector,
    data => (data.announcement || emptyObject),
);

export const EquityMutualFundTickerEquitySelector = createSelector(
    equityTickerDataSelector,
    data => (data.equity || emptyList).sort((a, b) => b.portWeight - a.portWeight),
);

export const EquityMutualFundTickerPercentageChangeChartSelector = createSelector(
    equityTickerDataSelector,
    data => (data.percentageChangeChart || emptyObject),
);

export const EquityMutualFundTickerSectorSelector = createSelector(
    equityTickerDataSelector,
    data => (data.sector || emptyList).sort((a, b) => b.value - a.value),
);

export const sectorsListSelector = createSelector(
    sectorsSelector,
    sectors => (
        Object.keys(sectors).map(sector => sectors[sector])
    ),
);

export const sectorsNepseSelector = createSelector(
    sectorsSelector,
    overviewActiveSectorSelector,
    (sectors, activeSector) => (sectors[activeSector] || emptyObject),
);

export const overviewTimeSeriesLineChartDataSelector = createSelector(
    overviewActiveSectorSelector,
    overviewTimeSeriesSelector,
    (sector, data) => ((data[sector] || emptyObject).lineChart || emptyObject),
);

export const overviewTimeSeriesBarChartDataSelector = createSelector(
    overviewActiveSectorSelector,
    overviewTimeSeriesSelector,
    (sector, data) => ((data[sector] || emptyObject).barChart || emptyObject),
);

export const moversLosersSelector = createSelector(
    moversSelector,
    data => data.gainers || emptyList,
);

export const moversGainersSelector = createSelector(
    moversSelector,
    data => data.losers || emptyList,
);



// WEBPACK FOOTER //
// ./src/common/selectors/domainData.js